import type * as React from "react"

import { cn } from "@/lib/utils"

const inputControlClassName =
  "text-small font-normal h-[var(--control-height)] w-full min-w-0 rounded-[var(--radius-control)] border-[0.5px] border-input-border bg-input px-3 py-1.5 text-foreground shadow-none selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 hover:border-foreground/20 focus-input aria-invalid:border-destructive"

const INPUT_PREVIEW_HOVER = "border-foreground/20"
const INPUT_PREVIEW_FOCUS =
  "outline outline-1 [outline-color:var(--input-focus-outline)] -outline-offset-1"

function Input({ className, type, ...props }: React.ComponentProps<"input">) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        inputControlClassName,
        "file:text-foreground file:text-mini file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium",
        className,
      )}
      {...props}
    />
  )
}

function InputPreviewCell({
  label,
  state,
  className,
}: {
  label: string
  state: "default" | "hover" | "focus" | "disabled"
  className?: string
}) {
  const stateClass =
    state === "hover"
      ? INPUT_PREVIEW_HOVER
      : state === "focus"
        ? INPUT_PREVIEW_FOCUS
        : undefined

  return (
    <input
      type="text"
      readOnly
      data-slot="input"
      data-preview-state={state === "default" ? undefined : state}
      disabled={state === "disabled"}
      placeholder={label}
      className={cn(
        inputControlClassName,
        stateClass,
        "pointer-events-none max-w-[160px]",
        className,
      )}
      tabIndex={-1}
      aria-label={`input ${label}`}
    />
  )
}

function InputPreviewRow({
  label,
  className,
}: {
  label: string
  className?: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-section-label text-muted-foreground w-24 shrink-0">
        {label}
      </span>
      <InputPreviewCell label="default" state="default" className={className} />
      <InputPreviewCell label="hover" state="hover" className={className} />
      <InputPreviewCell label="focus" state="focus" className={className} />
      <InputPreviewCell
        label="disabled"
        state="disabled"
        className={className}
      />
    </div>
  )
}

function TextareaPreviewCell({
  label,
  state,
}: {
  label: string
  state: "default" | "hover" | "focus"
}) {
  const stateClass =
    state === "hover"
      ? INPUT_PREVIEW_HOVER
      : state === "focus"
        ? INPUT_PREVIEW_FOCUS
        : undefined

  return (
    <textarea
      readOnly
      data-slot="textarea"
      data-preview-state={state === "default" ? undefined : state}
      placeholder={label}
      rows={2}
      className={cn(
        "text-small border-input-border bg-input text-foreground placeholder:text-muted-foreground hover:border-foreground/20 focus-input aria-invalid:border-destructive min-h-16 w-full max-w-[160px] rounded-[var(--radius-control)] border-[0.5px] px-3 py-1.5 font-normal shadow-none",
        stateClass,
        "pointer-events-none resize-none",
      )}
      tabIndex={-1}
      aria-label={`textarea ${label}`}
    />
  )
}

function TextareaPreviewRow() {
  return (
    <div className="flex flex-wrap items-start gap-2">
      <span className="text-section-label text-muted-foreground w-24 shrink-0 pt-2">
        Textarea
      </span>
      <TextareaPreviewCell label="default" state="default" />
      <TextareaPreviewCell label="hover" state="hover" />
      <TextareaPreviewCell label="focus" state="focus" />
    </div>
  )
}

function SelectPreviewCell({
  label,
  state,
}: {
  label: string
  state: "default" | "hover" | "focus" | "disabled"
}) {
  const stateClass =
    state === "hover"
      ? INPUT_PREVIEW_HOVER
      : state === "focus"
        ? INPUT_PREVIEW_FOCUS
        : undefined

  return (
    <div
      data-slot="select-trigger"
      data-preview-state={state === "default" ? undefined : state}
      data-size="default"
      aria-disabled={state === "disabled"}
      className={cn(
        "border-input-border data-[placeholder]:text-muted-foreground bg-input text-small hover:border-foreground/20 focus-input flex w-[160px] items-center justify-between gap-2 rounded-[var(--radius-control)] border-[0.5px] px-3 py-1.5 font-normal whitespace-nowrap shadow-none data-[size=default]:h-[var(--select-trigger-height)]",
        state === "disabled" ? "cursor-not-allowed opacity-50" : undefined,
        stateClass,
        "pointer-events-none",
      )}
      aria-label={`select ${label}`}
    >
      <span>Inventory</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-3.5 opacity-50"
        aria-hidden
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  )
}

function SelectPreviewRow() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-section-label text-muted-foreground w-24 shrink-0">
        Select
      </span>
      <SelectPreviewCell label="default" state="default" />
      <SelectPreviewCell label="hover" state="hover" />
      <SelectPreviewCell label="focus" state="focus" />
      <SelectPreviewCell label="disabled" state="disabled" />
    </div>
  )
}

function InputPreview() {
  return (
    <div
      data-slot="input-preview"
      className="border-border space-y-3 rounded-[var(--radius-control)] border-[0.5px] p-3"
    >
      <InputPreviewRow label="Input" />
      <SelectPreviewRow />
      <TextareaPreviewRow />
    </div>
  )
}

export { Input, InputPreview, inputControlClassName }
